import "./App.css";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
// user routes
import { Home } from "./components/Home/Home";
import { Login } from "./components/Login/Login";
import Register from "./components/Register/Register";
import { AuthProvider } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import UnprotectedRoute from "./auth/UnprotectedRoute";
import { Logout } from "./components/Logout/Logout";
import { Navbar } from "./components/Navbar/Navbar";
// admin routes
import { Admin } from "./components/AdminComponents/AdminInterface/Admin";
import { AddNewClass } from "./components/AdminComponents/AddNewClass/AddNewClass";
import { ClassPage } from "./components/AdminComponents/ClassPage/ClassPage";
import { Analytics } from "./components/AdminComponents/Analytics/Analytics";
// professor routes
import { ProfessorInterface } from "./components/ProfessorComponents/ProfessorInterface/ProfessorInterface";
import { Consent } from "./components/Consent/Consent";
import ChatContainer from "./components/Chat/ChatContainer";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  SystemContext,
  themeVariablesDark,
  themeVariablesLight,
} from "./Theme";
import { Footer } from "./components/Footer/Footer";
import { CssBaseline } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import React from "react";
import GoogleRedirectHandler from "./components/Google/GoogleRedirectHandler";
import { AnnotateDocument } from "./components/AdminComponents/AnnotatePDF/AnnotateDocument";
import { ClassEnroll } from "./components/AdminComponents/ClassEnroll/ClassEnroll";
import { Whitelist } from "./components/AdminComponents/Whitelist/Whitelist";
import { FeatureFlags } from "./components/AdminComponents/FeatureFlags/FeatureFlags";

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <UnprotectedRoute>
        <Home />
      </UnprotectedRoute>
    ),
  },
  {
    path: "/login",
    element: (
      <UnprotectedRoute>
        <Login />
      </UnprotectedRoute>
    ),
  },
  {
    path: "/auth/callback",
    element: (
      <UnprotectedRoute>
        <GoogleRedirectHandler />
      </UnprotectedRoute>
    ),
  },
  {
    path: "/logout",
    element: (
      <ProtectedRoute>
        <Logout />
      </ProtectedRoute>
    ),
  },
  {
    path: "/register",
    element: (
      <UnprotectedRoute>
        <Register />
      </UnprotectedRoute>
    ),
  },
  {
    path: "/consent",
    element: (
      <ProtectedRoute>
        <Consent />
      </ProtectedRoute>
    ),
  },
  {
    path: "/chat",
    element: (
      <ProtectedRoute>
        <Navbar />
        <ChatContainer />
        <Footer />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin",
    element: <Admin />,
  },
  {
    path: "/admin/:classID",
    element: <ClassPage />,
  },
  {
    path: "/admin/enroll/:classID",
    element: <ClassEnroll />,
  },
  {
    path: "/admin/add-new-class",
    element: <AddNewClass />,
  },
  {
    path: "/admin/:classID/:materialID",
    element: <ProfessorInterface />,
  },
  {
    path: "/admin/document/:classID/:documentID",
    element: <AnnotateDocument />,
  },

  {
    path: "/admin/analytics",
    element: <Analytics />,
  },
  {
    path: "/admin/whitelist",
    element: <Whitelist />,
  },
  {
    path: "/admin/feature-flags",
    element: <FeatureFlags />,
  },
  {
    path: "*",
    element: <div>Not found</div>,
  },
]);

const usePersistentMode = (initialMode = "light") => {
  // Initialize the state with the value from localStorage if it exists,
  // otherwise use the initial value provided.
  const [mode, setMode] = useState(() => {
    const storedMode = window.localStorage.getItem("mode");
    return storedMode !== null ? storedMode : initialMode;
  });

  // Use useEffect to update localStorage whenever the mode state changes.
  useEffect(() => {
    window.localStorage.setItem("mode", mode);
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  return { mode, toggleColorMode };
};

function App() {
  // theme
  const { mode, toggleColorMode } = usePersistentMode();

  // useMemo for theme
  const theme = useMemo(
    () =>
      createTheme(mode === "light" ? themeVariablesLight : themeVariablesDark),
    [mode]
  );

  // Assuming createTheme is a function that returns a theme object based on the current mode
  const colorMode = useMemo(
    () => ({
      toggleColorMode, // Directly passing toggleColorMode without wrapping it into another function
    }),
    [toggleColorMode]
  );

  const systemVariables = {
    systemName: "Kiwi ICP",
    ...colorMode,
  };

  return (
    <AuthProvider>
      <SystemContext.Provider value={systemVariables}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RouterProvider router={router} />
        </ThemeProvider>
      </SystemContext.Provider>
    </AuthProvider>
  );
}

export default App;
